import { db, auth } from '@/firebase/init';
import { doc, setDoc, getDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { deleteJourneyImages, deleteImagesByUrls } from './storageService';

/**
 * Journey Service - Manages personalization journey progress in Firebase
 */

/**
 * Save journey progress to Firebase
 * @param {string} journeyId - Unique journey identifier
 * @param {object} journeyData - Journey data to save
 * @returns {Promise<void>}
 */
/**
 * Remove undefined values from object recursively
 * Firebase doesn't allow undefined values
 */
function removeUndefinedValues(obj) {
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item)).filter(item => item !== undefined);
  }
  
  if (obj !== null && typeof obj === 'object') {
    const cleaned = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    });
    return cleaned;
  }
  
  return obj;
}

/**
 * Clean fullControlSets before saving to Firebase
 * Removes component props with example images, keeps only user data
 * Optimized to minimize storage size
 */
function cleanControlSetsForStorage(sets) {
  if (!Array.isArray(sets)) return sets;
  
  return sets.map(set => {
    const cleaned = {
      name: set.name,
      isComplete: set.isComplete || false,
      personalizationType: set.personalizationType || null,
      // Use personalization array
      personalization: []
    };
    
    // Add optional fields only if they exist
    if (set.professional !== undefined) {
      cleaned.professional = set.professional;
    }
    if (set.showComponent !== undefined) {
      cleaned.showComponent = set.showComponent;
    }
    if (set.createdAt) {
      cleaned.createdAt = set.createdAt;
    }
    if (set.updatedAt) {
      cleaned.updatedAt = set.updatedAt;
    }
    
    // Clean personalization entries - ensure image URLs are strings only (not File objects)
    if (Array.isArray(set.personalization)) {
      cleaned.personalization = set.personalization.map(entry => {
        const cleanedEntry = {
          type: entry.type,
          timestamp: entry.timestamp || Date.now()
        };
        
        // Handle different input types
        if (entry.type === 'imagePersonalization') {
          // New merged structure: array of inputs (uploaded, social, etc.)
          cleanedEntry.input = entry.input.map(inputItem => {
            const cleanedInput = {
              type: inputItem.type,
              timestamp: inputItem.timestamp || Date.now()
            };
            
            if (inputItem.type === 'uploaded') {
              // Clean uploaded image URLs
              cleanedInput.urls = (inputItem.urls || []).map(item => {
                if (typeof item === 'string') return item;
                if (item && item.url) return item.url;
                if (item && item.storageUrl) return item.storageUrl;
                return null;
              }).filter(Boolean);
              cleanedInput.multiplePets = inputItem.multiplePets || false;
              // Preserve multiplePetsList structure: [{name, species, imageIndices: [...]}]
              cleanedInput.multiplePetsList = (inputItem.multiplePetsList || []).map(pet => ({
                name: pet.name,
                species: pet.species,
                imageIndices: pet.imageIndices || [],
                hasImage: pet.hasImage || false
              }));
              cleanedInput.portraitStyle = inputItem.portraitStyle || false;
              cleanedInput.selectedOutfitName = inputItem.selectedOutfitName || null;
              cleanedInput.selectedOutfitStyle = inputItem.selectedOutfitStyle || null;
            } else if (inputItem.type === 'social') {
              // Social media data
              cleanedInput.platform = inputItem.platform || null;
              cleanedInput.username = inputItem.username || null;
              cleanedInput.multiplePets = inputItem.multiplePets || false;
              // Preserve multiplePetsList structure: [{name, species, imageIndices: [...]}]
              cleanedInput.multiplePetsList = (inputItem.multiplePetsList || []).map(pet => ({
                name: pet.name,
                species: pet.species,
                imageIndices: pet.imageIndices || [],
                hasImage: pet.hasImage || false
              }));
              cleanedInput.portraitStyle = inputItem.portraitStyle || false;
              cleanedInput.disclaimerData = inputItem.disclaimerData || null;
              cleanedInput.selectedOutfitName = inputItem.selectedOutfitName || null;
              cleanedInput.selectedOutfitStyle = inputItem.selectedOutfitStyle || null;
            }
            
            return cleanedInput;
          }).filter(item => item.type); // Remove invalid entries
        } else if (entry.type === 'pictures') {
          // Legacy format support: Old pictures format
          if (Array.isArray(entry.input)) {
            cleanedEntry.input = entry.input.map(item => {
              if (typeof item === 'string') return item;
              if (item && item.url) return item.url;
              if (item && item.storageUrl) return item.storageUrl;
              return null;
            }).filter(Boolean);
          } else if (entry.input && entry.input.urls) {
            cleanedEntry.input = {
              urls: entry.input.urls.map(item => {
                if (typeof item === 'string') return item;
                if (item && item.url) return item.url;
                if (item && item.storageUrl) return item.storageUrl;
                return null;
              }).filter(Boolean),
              multiplePets: entry.input.multiplePets || false,
              multiplePetsList: entry.input.multiplePetsList || [],
              portraitStyle: entry.input.portraitStyle || false,
              selectedOutfitName: entry.input.selectedOutfitName || null,
              selectedOutfitStyle: entry.input.selectedOutfitStyle || null
            };
          }
        } else if (entry.type === 'socialMedia') {
          // Legacy format support: Old socialMedia format
          cleanedEntry.input = {
            platform: entry.input.platform,
            username: entry.input.username,
            multiplePets: entry.input.multiplePets || false,
            multiplePetsList: entry.input.multiplePetsList || [],
            portraitStyle: entry.input.portraitStyle || false,
            disclaimerData: entry.input.disclaimerData || null,
            selectedOutfitName: entry.input.selectedOutfitName || null,
            selectedOutfitStyle: entry.input.selectedOutfitStyle || null
          };
        } else {
          // For text, color, phoneModel, etc. - save as is
          cleanedEntry.input = entry.input;
        }
        
        return cleanedEntry;
      });
    }
    
    return cleaned;
  });
}

export async function saveJourneyProgress(journeyId, journeyData) {
  console.log('🔵 [JOURNEY SERVICE] saveJourneyProgress called');
  console.log('🔵 [JOURNEY SERVICE] Journey ID:', journeyId);
  console.log('🔵 [JOURNEY SERVICE] Journey data keys:', Object.keys(journeyData));
  
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('⚠️ [JOURNEY SERVICE] User not authenticated, cannot save journey');
      return;
    }

    console.log('🔵 [JOURNEY SERVICE] User authenticated:', user.uid);
    
    const journeyRef = doc(db, 'journeys', `${user.uid}_${journeyId}`);
    console.log('🔵 [JOURNEY SERVICE] Firestore document path:', `journeys/${user.uid}_${journeyId}`);
    
    // Ensure images are properly serialized (remove File objects, keep URLs)
    let imagesToSave = journeyData.images;
    let imageUrlsToSave = journeyData.imageUrls || [];
    
    console.log('🔵 [JOURNEY SERVICE] Images to save:', imagesToSave?.length || 0);
    console.log('🔵 [JOURNEY SERVICE] Image URLs to save:', imageUrlsToSave?.length || 0);
    
    // If images contain File objects with URLs, extract the URLs
    if (Array.isArray(imagesToSave)) {
      imageUrlsToSave = imagesToSave
        .map(img => {
          if (typeof img === 'string') return img;
          if (img && img.url) return img.url;
          return null;
        })
        .filter(Boolean);
    }
    
    console.log('📦 SAVE - Before cleaning:', {
      totalProducts: journeyData.fullControlSets?.length || 0,
      productsWithData: journeyData.fullControlSets?.filter(s => s.personalization?.length > 0).length || 0
    });
    
    const dataToSave = {
      ...journeyData,
      journeyId,
      userId: user.uid,
      userEmail: user.email,
      updatedAt: Date.now(),
      status: journeyData.isComplete ? 'FINISHED' : 'IN_PROGRESS',
      imageUrls: imageUrlsToSave, // Journey-level images (PersonalizationView)
      images: imageUrlsToSave.length > 0 ? imageUrlsToSave : (journeyData.images || []),
      // Clean fullControlSets before saving (remove example images)
      fullControlSets: cleanControlSetsForStorage(journeyData.fullControlSets)
    };
    
    // Remove undefined values (Firebase doesn't allow them)
    console.log('🔵 [JOURNEY SERVICE] Removing undefined values...');
    const cleanedData = removeUndefinedValues(dataToSave);
    
    console.log('🔵 [JOURNEY SERVICE] Data cleaned, keys:', Object.keys(cleanedData));
    console.log('🔵 [JOURNEY SERVICE] Writing to Firestore...');

    await setDoc(journeyRef, cleanedData, { merge: true });
    
    console.log('✅ [JOURNEY SERVICE] Firestore write complete');
    
    // Essential tracking log
    const productsWithData = cleanedData.fullControlSets?.filter(s => s.personalization?.length > 0) || [];
    console.log(`✅ SAVED to Firebase: ${journeyId}`);
    console.log(`   ${productsWithData.length}/${cleanedData.fullControlSets?.length || 0} products have personalization data`);
    
    // Detailed log for products with data
    productsWithData.forEach(set => {
      // Check for new imagePersonalization structure
      const imagePersonalizationEntry = set.personalization.find(e => e.type === 'imagePersonalization');
      let imageCount = 0;
      let hasSocialMedia = false;
      
      if (imagePersonalizationEntry && Array.isArray(imagePersonalizationEntry.input)) {
        imagePersonalizationEntry.input.forEach(inputItem => {
          if (inputItem.type === 'uploaded' && inputItem.urls) {
            imageCount += inputItem.urls.length;
          } else if (inputItem.type === 'social') {
            hasSocialMedia = true;
          }
        });
      }
      
      // Legacy format support
      const picturesEntry = set.personalization.find(e => e.type === 'pictures');
      if (picturesEntry) {
        if (Array.isArray(picturesEntry.input)) {
          imageCount += picturesEntry.input.length;
        } else if (picturesEntry.input?.urls) {
          imageCount += picturesEntry.input.urls.length;
        }
      }
      
      // Check other types
      if (!hasSocialMedia) {
        hasSocialMedia = set.personalization.some(e => e.type === 'socialMedia');
      }
      const hasText = set.personalization.some(e => e.type === 'text');
      const hasColor = set.personalization.some(e => e.type === 'color');
      const hasPhoneModel = set.personalization.some(e => e.type === 'phoneModel');
      
      const details = [];
      if (imageCount > 0) details.push(`${imageCount} images`);
      if (hasSocialMedia) details.push('socialMedia');
      if (hasText) details.push('text');
      if (hasColor) details.push('color');
      if (hasPhoneModel) details.push('phoneModel');
      
      console.log(`   📦 ${set.name} (${set.personalizationType}): ${details.join(', ')}`);
    });
    console.log('✅ [JOURNEY SERVICE] saveJourneyProgress complete');
    return dataToSave;
  } catch (error) {
    console.error('❌ [JOURNEY SERVICE] Error saving journey progress:', error);
    console.error('❌ [JOURNEY SERVICE] Error details:', error.message);
    console.error('❌ [JOURNEY SERVICE] Error stack:', error.stack);
    throw error;
  }
}

/**
 * Load journey progress from Firebase
 * @param {string} journeyId - Journey identifier
 * @returns {Promise<object|null>}
 */
export async function loadJourneyProgress(journeyId) {
  try {
    const user = auth.currentUser;
    if (!user) return null;

    const journeyRef = doc(db, 'journeys', `${user.uid}_${journeyId}`);
    const snap = await getDoc(journeyRef);

    if (snap.exists()) {
      return snap.data();
    }
    return null;
  } catch (error) {
    console.error('Error loading journey progress:', error);
    return null;
  }
}

/**
 * Check if there's an in-progress journey for a product
 * @param {string} productId - Base product ID (without timestamp)
 * @returns {Promise<object|null>}
 */
export async function checkInProgressJourney(productId) {
  try {
    const user = auth.currentUser;
    if (!user) return null;

    const journeysRef = collection(db, 'journeys');
    const q = query(
      journeysRef,
      where('userId', '==', user.uid),
      where('status', '==', 'IN_PROGRESS')
    );

    const snapshot = await getDocs(q);
    
    // Find journey matching the base product ID
    for (const doc of snapshot.docs) {
      const data = doc.data();
      const baseId = String(data.productId || data.journeyId || '').split('-')[0];
      if (baseId === String(productId)) {
        // Validate that journey has meaningful progress data
        const hasProgress = 
          (data.step && data.step > 0) || // Has progressed beyond step 0
          (data.images && data.images.length > 0) || // Has uploaded images
          (data.imageUrls && data.imageUrls.length > 0) || // Has image URLs
          (data.fullControlSets && data.fullControlSets.length > 0) || // Has customization data
          data.petName || // Has pet name
          data.profileType || // Has profile type
          data.theme; // Has selected theme
        
        // Only return journey if it has actual progress
        if (hasProgress) {
          return { id: doc.id, ...data };
        } else {
          // Journey exists but has no progress - delete it silently
          console.log('Found empty journey, cleaning up:', doc.id);
          await deleteJourney(data.productId || data.journeyId);
          return null;
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error checking in-progress journey:', error);
    return null;
  }
}

/**
 * Get all journeys for current user
 * @param {string} status - Filter by status: 'IN_PROGRESS', 'FINISHED', or null for all
 * @returns {Promise<Array>}
 */
export async function getUserJourneys(status = null) {
  try {
    const user = auth.currentUser;
    if (!user) return [];

    const journeysRef = collection(db, 'journeys');
    let q;
    
    if (status) {
      q = query(
        journeysRef,
        where('userId', '==', user.uid),
        where('status', '==', status)
      );
    } else {
      q = query(journeysRef, where('userId', '==', user.uid));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting user journeys:', error);
    return [];
  }
}

/**
 * Delete a journey and all associated images
 * @param {string} journeyId - Journey identifier
 * @returns {Promise<void>}
 */
export async function deleteJourney(journeyId) {
  try {
    const user = auth.currentUser;
    if (!user) return;

    const journeyRef = doc(db, 'journeys', `${user.uid}_${journeyId}`);
    
    // First, get the journey data to find all images
    const journeySnap = await getDoc(journeyRef);
    
    if (journeySnap.exists()) {
      const journeyData = journeySnap.data();
      
      // Collect all image URLs to delete
      const imageUrlsToDelete = [];
      
      // Add journey-level images
      if (journeyData.imageUrls && Array.isArray(journeyData.imageUrls)) {
        imageUrlsToDelete.push(...journeyData.imageUrls);
      }
      if (journeyData.images && Array.isArray(journeyData.images)) {
        imageUrlsToDelete.push(...journeyData.images.filter(img => typeof img === 'string'));
      }
      
      // Extract images from personalization array in fullControlSets
      if (journeyData.fullControlSets && Array.isArray(journeyData.fullControlSets)) {
        journeyData.fullControlSets.forEach(set => {
          if (set.personalization && Array.isArray(set.personalization)) {
            set.personalization.forEach(entry => {
              if (entry.type === 'pictures' && Array.isArray(entry.input)) {
                imageUrlsToDelete.push(...entry.input.filter(url => typeof url === 'string'));
              }
            });
          }
        });
      }
      
      // Delete images from Storage by URL
      if (imageUrlsToDelete.length > 0) {
        console.log(`Deleting ${imageUrlsToDelete.length} images for journey ${journeyId}`);
        await deleteImagesByUrls(imageUrlsToDelete);
      }
      
      // Also delete by folder path (catches any images not in the URLs list)
      await deleteJourneyImages(user.uid, journeyId);
    }
    
    // Delete the journey document from Firestore
    await deleteDoc(journeyRef);
    console.log('Journey and all images deleted:', journeyId);
  } catch (error) {
    console.error('Error deleting journey:', error);
    throw error;
  }
}

/**
 * Mark journey as complete
 * @param {string} journeyId - Journey identifier
 * @returns {Promise<void>}
 */
export async function completeJourney(journeyId) {
  try {
    const user = auth.currentUser;
    if (!user) return;

    const journeyRef = doc(db, 'journeys', `${user.uid}_${journeyId}`);
    await setDoc(journeyRef, {
      status: 'FINISHED',
      isComplete: true,
      completedAt: Date.now(),
      updatedAt: Date.now()
    }, { merge: true });
    
    console.log('Journey completed:', journeyId);
  } catch (error) {
    console.error('Error completing journey:', error);
    throw error;
  }
}

/**
 * Sync Vuex journey state to Firebase
 * @param {object} store - Vuex store instance
 * @param {string} journeyId - Journey identifier
 * @returns {Promise<void>}
 */
export async function syncJourneyToFirebase(store, journeyId) {
  try {
    const journey = store.getters.getJourneyByProductId(journeyId);
    if (!journey) return;

    await saveJourneyProgress(journeyId, journey);
  } catch (error) {
    console.error('Error syncing journey to Firebase:', error);
  }
}

/**
 * Restore journey from Firebase to Vuex
 * @param {object} store - Vuex store instance
 * @param {string} journeyId - Journey identifier
 * @returns {Promise<boolean>} - True if restored successfully
 */
export async function restoreJourneyFromFirebase(store, journeyId) {
  try {
    const journeyData = await loadJourneyProgress(journeyId);
    if (!journeyData) return false;

    // Restore to Vuex store
    store.dispatch('set_journey_progress', journeyData);
    return true;
  } catch (error) {
    console.error('Error restoring journey from Firebase:', error);
    return false;
  }
}
